import { Button, ButtonProps, Flex, InputProps, Text } from "@chakra-ui/react";
import React from "react";
import { ClosableTag } from "../Feedback/ClosableTag";
import { CustomFormInputProps } from "../Inputs/CustomFormInput";
import { FormInput } from "./FormInput";

type InputWithTagsBaseProps = {
  /** The label text for the input field */
  label: string;
  /** Error message to display below the input */
  error?: string;
  /** Helper text to display below the input */
  helperText?: string;
};

type InputWithTagsValueProps = {
  /** Initial values for the tags */
  initialValues: string[];
  /** Callback function called when tags are added or removed */
  onChange: (values: string[]) => void;
};

type InputWithTagsInputProps = {
  /** Props for the input field */
  inputProps?: Omit<CustomFormInputProps, "children"> & InputProps;
};

type InputWithTagsButtonProps = {
  /** Props for the add button */
  addButtonProps?: ButtonProps;
};

type InputWithTagsProps = InputWithTagsBaseProps &
  InputWithTagsValueProps &
  InputWithTagsInputProps &
  InputWithTagsButtonProps;

export const InputWithTags: React.FC<InputWithTagsProps> = ({
  label,
  error,
  onChange,
  helperText,
  inputProps,
  addButtonProps,
  initialValues,
}) => {
  const [value, setValue] = React.useState("");
  const [selectedItems, setSelectedItems] =
    React.useState<string[]>(initialValues);
  const addButtonClicked = React.useCallback(() => {
    if (!value) {
      return;
    }
    const newSelectedItems = value.split(",").map((item) => item.trim());
    const uniqueItems = newSelectedItems.filter(
      (item, index, self) => self.indexOf(item) === index
    );
    const filteredItems = uniqueItems.filter(
      (item) => !selectedItems.includes(item)
    );

    setSelectedItems([...selectedItems, ...filteredItems]);
    onChange([...selectedItems, ...filteredItems]);
    setValue("");
  }, [selectedItems, value, onChange]);

  const onItemRemoved = React.useCallback(
    (item: string) => {
      setSelectedItems(selectedItems.filter((i) => i !== item));
      onChange(selectedItems.filter((i) => i !== item));
    },
    [selectedItems, onChange]
  );

  return (
    <Flex direction="column" gap={2}>
      <Text fontSize="sm">{label}</Text>
      {selectedItems.length > 0 && (
        <Flex gap={2} wrap="wrap">
          {selectedItems.map((item) => (
            <ClosableTag
              key={item}
              label={item}
              onClose={() => {
                if (onItemRemoved) onItemRemoved(item);
              }}
              size={"sm"}
            />
          ))}
        </Flex>
      )}
      <FormInput
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={error}
        formLabelProps={{ color: "gray.600" }}
        formControlProps={{ height: "unset" }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            addButtonClicked();
          }
        }}
        {...inputProps}
      />
      {helperText && (
        <Text color="gray.600" fontSize="sm">
          {helperText}
        </Text>
      )}

      <Button
        size="sm"
        variant="outline"
        colorScheme="gray"
        onClick={addButtonClicked}
        w="fit-content"
        {...addButtonProps}
      >
        {addButtonProps?.children ?? "Add"}
      </Button>
    </Flex>
  );
};
